export type RealtyOrder = 'asc' | 'desc';
export type RealtyOrderBy = 'id' | 'price';

export type RealtyFilter = {
  type?: string;
  offer_type?: number[];
  price_from?: number;
  price_to?: number;
  area_from?: number;
  area_to?: number;
  is_new?: boolean;
  city?: string;
  village?: string;
  number_of_rooms?: number[];
  orderBy?: RealtyOrderBy;
  order?: RealtyOrder;
  search?: string;
};

export type RealtySearchParamsReader = Pick<URLSearchParams, 'get' | 'getAll'>;

export const DEFAULT_REALTY_ORDER_BY: RealtyOrderBy = 'id';
export const DEFAULT_REALTY_ORDER: RealtyOrder = 'desc';

export const DEFAULT_REALTY_FILTER: Readonly<RealtyFilter> = {
  orderBy: DEFAULT_REALTY_ORDER_BY,
  order: DEFAULT_REALTY_ORDER,
};

export const REALTY_FILTER_QUERY_KEYS = [
  'type',
  'offer_type',
  'price_from',
  'price_to',
  'area_from',
  'area_to',
  'is_new',
  'city',
  'village',
  'number_of_rooms',
  'orderBy',
  'order',
  'search',
  'location',
  'location_id',
] as const;

const parseFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** URL controls accept decimal commas, but blank/null values are never zero. */
export const parseRealtyFilterNumber = (value: unknown): number | undefined => {
  const parsed = parseFiniteNumber(
    typeof value === 'string' ? value.trim().replace(',', '.') : value,
  );
  return parsed !== undefined && parsed >= 0 ? parsed : undefined;
};

const parseInteger = (value: unknown, minimum: number): number | undefined => {
  const parsed = parseFiniteNumber(value);
  if (parsed === undefined || !Number.isInteger(parsed) || parsed < minimum) return undefined;
  return parsed;
};

const normalizeIntegerList = (
  values: readonly unknown[] | undefined,
  minimum: number,
  maximum?: number,
) => {
  const normalized = (values ?? [])
    .map(value => parseInteger(value, minimum))
    .filter(
      (value): value is number =>
        value !== undefined && (maximum === undefined || value <= maximum),
    );

  return [...new Set(normalized)].sort((left, right) => left - right);
};

const normalizeOptionalId = (value: unknown) => {
  const normalized = parseInteger(value, 1);
  return normalized === undefined ? undefined : normalized.toString();
};

const normalizeRange = (fromValue: unknown, toValue: unknown) => {
  let from = parseRealtyFilterNumber(fromValue);
  let to = parseRealtyFilterNumber(toValue);

  if (from !== undefined && to !== undefined && from > to) {
    [from, to] = [to, from];
  }

  return { from, to };
};

const normalizeOrderBy = (value: unknown): RealtyOrderBy =>
  value === 'price' || value === 'id' ? value : DEFAULT_REALTY_ORDER_BY;

const normalizeOrder = (value: unknown): RealtyOrder =>
  value === 'asc' || value === 'desc' ? value : DEFAULT_REALTY_ORDER;

export const normalizeRealtyFilter = (filter: Partial<RealtyFilter>): RealtyFilter => {
  const offerType = normalizeIntegerList(filter.offer_type, 1);
  // The storefront search document uses rooms_count=0 for studio/no-room
  // records. Values 1–4 are exact counts and 5 is the UI sentinel for 5+.
  const numberOfRooms = normalizeIntegerList(filter.number_of_rooms, 0, 5);
  const price = normalizeRange(filter.price_from, filter.price_to);
  const area = normalizeRange(filter.area_from, filter.area_to);
  const search = filter.search?.trim();

  return {
    type: normalizeOptionalId(filter.type),
    offer_type: offerType.length > 0 ? offerType : undefined,
    price_from: price.from,
    price_to: price.to,
    area_from: area.from,
    area_to: area.to,
    is_new: filter.is_new === true ? true : undefined,
    city: normalizeOptionalId(filter.city),
    village: normalizeOptionalId(filter.village),
    number_of_rooms: numberOfRooms.length > 0 ? numberOfRooms : undefined,
    orderBy: normalizeOrderBy(filter.orderBy),
    order: normalizeOrder(filter.order),
    search: search ? search : undefined,
  };
};

const firstPresentValue = (searchParams: RealtySearchParamsReader, keys: readonly string[]) => {
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value !== null && value.trim() !== '') return value;
  }

  return undefined;
};

export const parseRealtySearchParams = (searchParams: RealtySearchParamsReader): RealtyFilter =>
  normalizeRealtyFilter({
    type: searchParams.get('type') ?? undefined,
    offer_type: searchParams.getAll('offer_type').map(Number),
    price_from: parseRealtyFilterNumber(searchParams.get('price_from')),
    price_to: parseRealtyFilterNumber(searchParams.get('price_to')),
    area_from: parseRealtyFilterNumber(searchParams.get('area_from')),
    area_to: parseRealtyFilterNumber(searchParams.get('area_to')),
    is_new: searchParams.get('is_new') === 'true',
    city: firstPresentValue(searchParams, ['city', 'location', 'location_id']),
    village: searchParams.get('village') ?? undefined,
    number_of_rooms: searchParams.getAll('number_of_rooms').map(Number),
    orderBy: (searchParams.get('orderBy') as RealtyOrderBy | null) ?? undefined,
    order: (searchParams.get('order') as RealtyOrder | null) ?? undefined,
    search: searchParams.get('search') ?? undefined,
  });

export const serializeRealtyFilter = (filter: Partial<RealtyFilter>): string => {
  const normalized = normalizeRealtyFilter(filter);
  const searchParams = new URLSearchParams();

  if (normalized.type) searchParams.set('type', normalized.type);
  normalized.offer_type?.forEach(value => searchParams.append('offer_type', value.toString()));
  if (normalized.price_from !== undefined)
    searchParams.set('price_from', normalized.price_from.toString());
  if (normalized.price_to !== undefined)
    searchParams.set('price_to', normalized.price_to.toString());
  if (normalized.city) searchParams.set('city', normalized.city);
  if (normalized.village) searchParams.set('village', normalized.village);
  normalized.number_of_rooms?.forEach(value =>
    searchParams.append('number_of_rooms', value.toString()),
  );
  if (normalized.is_new) searchParams.set('is_new', 'true');
  if (normalized.area_from !== undefined)
    searchParams.set('area_from', normalized.area_from.toString());
  if (normalized.area_to !== undefined) searchParams.set('area_to', normalized.area_to.toString());
  if (normalized.search) searchParams.set('search', normalized.search);

  if (normalized.orderBy !== DEFAULT_REALTY_ORDER_BY || normalized.order !== DEFAULT_REALTY_ORDER) {
    searchParams.set('orderBy', normalized.orderBy ?? DEFAULT_REALTY_ORDER_BY);
    searchParams.set('order', normalized.order ?? DEFAULT_REALTY_ORDER);
  }

  return searchParams.toString();
};

export const realtyFiltersEqual = (left: Partial<RealtyFilter>, right: Partial<RealtyFilter>) =>
  serializeRealtyFilter(left) === serializeRealtyFilter(right);

export const buildRealtySortTransition = (
  draftFilter: Partial<RealtyFilter>,
  appliedFilter: Partial<RealtyFilter>,
  orderBy: RealtyOrderBy,
  order: RealtyOrder,
) => ({
  draftFilter: normalizeRealtyFilter({ ...draftFilter, orderBy, order }),
  appliedFilter: normalizeRealtyFilter({ ...appliedFilter, orderBy, order }),
});

export const constrainRealtyFilterCapabilities = (
  filter: Partial<RealtyFilter>,
  capabilities: { underConstruction: boolean },
) => {
  const normalized = normalizeRealtyFilter(filter);

  return capabilities.underConstruction
    ? normalized
    : normalizeRealtyFilter({ ...normalized, is_new: undefined });
};

export const mergeRealtyFilterSearchParams = (
  currentSearch: string | URLSearchParams,
  filter: Partial<RealtyFilter>,
) => {
  const merged = new URLSearchParams(currentSearch);
  REALTY_FILTER_QUERY_KEYS.forEach(key => merged.delete(key));

  const canonical = new URLSearchParams(serializeRealtyFilter(filter));
  canonical.forEach((value, key) => merged.append(key, value));

  return merged.toString();
};

export const buildRealtyHistoryUrl = (pathname: string, search: string, hash: string) => {
  const normalizedSearch = search && !search.startsWith('?') ? `?${search}` : search;

  return `${pathname}${normalizedSearch}${hash}`;
};

export const clearRealtyFilter = (filter: Partial<RealtyFilter>): RealtyFilter =>
  normalizeRealtyFilter({
    type: filter.type,
    orderBy: filter.orderBy,
    order: filter.order,
  });
